import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { SimpleSidebarHeader } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { DeliveryHistory } from "./delivery-history";
import { NoteComposer } from "./note-composer";
import type { PollingResult } from "./polling";
import { SIDEBAR_VIEW, type SidebarView } from "./sidebar-view";
import { SidebarViewToggle } from "./sidebar-view-toggle";
import { StatementPreview } from "./statement-preview";
import type {
  PayrollStatementNotificationRecipient,
  PayrollStatementProjectionBuild,
} from "./types";
import { getNotificationReasonLabel } from "./utils";

export const TechnicianDetailSidebar = ({
  businessId,
  periodStart,
  recipient,
  view,
  onViewChange,
  onClose,
  noteDraft,
  onNoteChange,
  previewOnly,
  projectionBuild,
}: {
  businessId: string | null;
  periodStart: string | null;
  recipient: PayrollStatementNotificationRecipient | null;
  view: SidebarView;
  onViewChange: (view: SidebarView) => void;
  onClose: () => void;
  noteDraft: string;
  onNoteChange: (value: string) => void;
  previewOnly: boolean;
  projectionBuild: PollingResult<PayrollStatementProjectionBuild> | null;
}) => {
  if (!recipient) return null;

  const ineligibleReason = getNotificationReasonLabel(
    recipient.ineligible_reason,
  );

  return (
    <Stack
      align="start"
      className={cn("w-full", "h-full min-h-0", { "opacity-60": previewOnly })}
      gap={0}
    >
      <SimpleSidebarHeader
        title={recipient.name}
        actions={<SidebarViewToggle view={view} onViewChange={onViewChange} />}
        onClose={onClose}
      />

      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        {view === SIDEBAR_VIEW.history ? (
          <div className="pt-4">
            {!recipient.eligible && ineligibleReason && (
              <div className="px-5 pb-2">
                <Alert color="warning" role="status">
                  <WarningIcon />
                  <AlertDescription>
                    <Text variant="caption">
                      This technician can't be emailed yet.
                    </Text>
                    <Text tone="muted">{ineligibleReason}</Text>
                  </AlertDescription>
                </Alert>
              </div>
            )}
            {recipient.eligible && (
              <Stack align="start" gap={1} className="w-full px-5 pb-5">
                <Text
                  variant="caption"
                  className="font-medium tracking-widest text-subtle uppercase"
                >
                  Note for the next send (optional)
                </Text>
                <NoteComposer
                  businessId={businessId}
                  periodStart={periodStart}
                  technicianId={recipient.technician_id}
                  value={noteDraft}
                  onChange={onNoteChange}
                  placeholder={`Hey ${recipient.name.split(" ")[0]}, here's what changed...`}
                  disabled={previewOnly}
                />
              </Stack>
            )}
            <DeliveryHistory deliveries={recipient.delivery_history} />
          </div>
        ) : (
          <StatementPreview
            recipient={recipient}
            projectionBuild={projectionBuild}
          />
        )}
      </div>
    </Stack>
  );
};
